import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../../lib/api";


import CategoryBadge from "./components/CategoryBadge";
import ResearchCTA from "./ResearchCTA";
import { Download, ArrowLeft } from "lucide-react";

const CATEGORY_LABEL = {
  "findings":     "Findings",
  "teardowns":    "Teardowns",
  "reports":      "Industry Reports",
  "case-studies": "Case Studies",
  "methodology":  "Methodology",
  "resources":    "Resources",
};

export default function ResearchArticle() {
  const { category, slug } = useParams();
  const navigate = useNavigate();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/research/publications/${slug}`);
        setPub(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Publication not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080807] text-[#f4efe6]">
  
        <main className="max-w-3xl mx-auto px-6 sm:px-8 py-24"><p className="text-[#8a7e6d]">Loading…</p></main>
      </div>
    );
  }

  if (error || !pub) {
    return (
      <div className="min-h-screen bg-[#080807] text-[#f4efe6]">
  
        <main className="max-w-3xl mx-auto px-6 sm:px-8 py-24">
          <h1 className="font-serif-display text-3xl">Publication not found</h1>
          <button onClick={() => navigate("/research")} className="mt-4 text-[#b8963e] hover:underline">← Back to Research</button>
        </main>
  
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": pub.title,
    "description": pub.excerpt || pub.seo?.meta_description,
    "datePublished": pub.publish_at || pub.created_at,
    "dateModified": pub.updated_at,
    "author": {
      "@type": "Organization",
      "name": "The Entity Agency",
      "url": "https://theentityagency.com",
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Entity Agency",
      "url": "https://theentityagency.com",
    },
    "mainEntityOfPage": `https://theentityagency.com/research/${pub.category}/${pub.slug}`,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Research",                   "item": "https://theentityagency.com/research" },
      { "@type": "ListItem", "position": 2, "name": CATEGORY_LABEL[pub.category], "item": `https://theentityagency.com/research/${pub.category}` },
      { "@type": "ListItem", "position": 3, "name": pub.title,                    "item": `https://theentityagency.com/research/${pub.category}/${pub.slug}` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6]" data-testid="research-article-page">
      <Helmet>
        <title>{pub.seo?.meta_title || `${pub.title} — The AI Visibility Index™`}</title>
        <meta name="description" content={pub.seo?.meta_description || pub.excerpt} />
        <meta property="og:title" content={pub.title} />
        <meta property="og:description" content={pub.excerpt} />
        <meta property="og:type" content="article" />
        {pub.cover_image_data_url && <meta property="og:image" content={pub.cover_image_data_url} />}
        <link rel="canonical" href={`https://theentityagency.com/research/${pub.category}/${pub.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>



      <article className="border-b border-[#2a2620]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
          <nav className="font-mono-eav text-[11px] tracking-[0.2em] uppercase text-[#8a7e6d]" aria-label="Breadcrumb">
            <Link to="/research" className="hover:text-[#b8963e]">Research</Link>
            <span className="mx-2">/</span>
            <Link to={`/research/${pub.category}`} className="hover:text-[#b8963e]">{CATEGORY_LABEL[pub.category]}</Link>
          </nav>

          <CategoryBadge category={pub.category} className="mt-6 block" />
          <h1 className="mt-4 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-tight"
              style={{ fontWeight: 500 }}>
            {pub.title}
          </h1>
          {pub.subtitle && (
            <p className="mt-4 text-xl text-[#c9bca4] leading-relaxed">{pub.subtitle}</p>
          )}

          {/* Meta line */}
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono-eav text-[11px] tracking-[0.18em] uppercase text-[#8a7e6d]">
            {pub.publish_at && (
              <span data-testid="research-article-date">
                {new Date(pub.publish_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
              </span>
            )}
            {pub.industry && <span data-testid="research-article-industry">{pub.industry}</span>}
            {pub.platforms_tested?.length > 0 && (
              <span data-testid="research-article-platforms">{pub.platforms_tested.join(" · ")}</span>
            )}
          </div>

          {pub.cover_image_data_url && (
            <div className="mt-10 -mx-6 sm:mx-0 rounded-sm overflow-hidden border border-[#2a2620]">
              <img src={pub.cover_image_data_url} alt={pub.title} className="w-full h-auto" />
            </div>
          )}

          {pub.pdf_attachment_data_url && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/research/publications/${pub.slug}/pdf`}
               data-testid="research-article-pdf-download"
               className="mt-8 inline-flex items-center gap-2 px-4 py-2 border border-[#b8963e] text-[#b8963e] hover:bg-[#b8963e] hover:text-[#0c0a08] transition-colors font-mono-eav text-xs tracking-[0.18em] uppercase rounded-sm">
              <Download className="w-4 h-4" /> Download PDF
            </a>
          )}

          {/* Category-specific structured blocks */}
          {pub.category === "findings" && (
            <FindingBlocks pub={pub} />
          )}
          {pub.category === "teardowns" && (
            <TeardownBlocks pub={pub} />
          )}
          {pub.category === "reports" && (
            <ReportBlocks pub={pub} />
          )}
          {pub.category === "case-studies" && (
            <CaseStudyBlocks pub={pub} />
          )}

          {/* Body HTML */}
          {pub.body_html && (
            <div
              data-testid="research-article-body"
              className="research-body mt-10 text-[#c9bca4]"
              dangerouslySetInnerHTML={{ __html: pub.body_html }}
            />
          )}
        </div>
      </article>

      <ResearchCTA />

      <div className="max-w-3xl mx-auto px-6 sm:px-8 pb-16">
        <Link to={`/research/${pub.category}`}
              data-testid="research-article-back"
              className="inline-flex items-center gap-2 text-sm text-[#b8963e] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to {CATEGORY_LABEL[pub.category]}
        </Link>
      </div>


    </div>
  );
}


function StatBlock({ label, value, accent }) {
  return (
    <div className="border border-[#2a2620] rounded-sm p-5" data-testid={`research-stat-${label}`}>
      <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">{label}</p>
      <p className={`mt-2 font-serif-display text-3xl ${accent ? "text-[#b8963e]" : "text-[#f4efe6]"}`}>{value}</p>
    </div>
  );
}


function FindingBlocks({ pub }) {
  if (!pub.finding_summary && !pub.business_implication) return null;
  return (
    <div className="mt-10 space-y-6" data-testid="research-finding-blocks">
      {pub.finding_summary && (
        <div className="border-l-2 border-[#b8963e] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Finding Summary</p>
          <p className="mt-2 text-[#f4efe6] leading-relaxed">{pub.finding_summary}</p>
        </div>
      )}
      {pub.business_implication && (
        <div className="border-l-2 border-[#5e5448] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">Business Implication</p>
          <p className="mt-2 text-[#c9bca4] leading-relaxed">{pub.business_implication}</p>
        </div>
      )}
    </div>
  );
}


function TeardownBlocks({ pub }) {
  return (
    <div className="mt-10 space-y-8" data-testid="research-teardown-blocks">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border border-[#2a2620] rounded-sm p-5">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">Company A</p>
          <p className="mt-2 font-serif-display text-2xl text-[#f4efe6]">{pub.company_a}</p>
          {pub.score_a != null && (
            <p className="mt-3 font-mono-eav text-[11px] tracking-[0.18em] text-[#b8963e]">
              AI VISIBILITY SCORE™ — <span className="text-[#f4efe6] text-lg">{pub.score_a}</span>/100
            </p>
          )}
        </div>
        <div className="border border-[#2a2620] rounded-sm p-5">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">Company B</p>
          <p className="mt-2 font-serif-display text-2xl text-[#f4efe6]">{pub.company_b}</p>
          {pub.score_b != null && (
            <p className="mt-3 font-mono-eav text-[11px] tracking-[0.18em] text-[#b8963e]">
              AI VISIBILITY SCORE™ — <span className="text-[#f4efe6] text-lg">{pub.score_b}</span>/100
            </p>
          )}
        </div>
      </div>

      <ComparisonRow label="Recognition"            a={pub.recognition_a} b={pub.recognition_b} />
      <ComparisonRow label="Category Inclusion"     a={pub.category_a}    b={pub.category_b} />
      <ComparisonRow label="Competitive Association" a={pub.competitive_a} b={pub.competitive_b} />

      {pub.key_takeaway && (
        <div className="border-l-2 border-[#b8963e] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Key takeaway</p>
          <p className="mt-2 text-[#f4efe6] leading-relaxed"
             dangerouslySetInnerHTML={{ __html: pub.key_takeaway }} />
        </div>
      )}
    </div>
  );
}


function ComparisonRow({ label, a, b }) {
  if (!a && !b) return null;
  return (
    <div>
      <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">{label}</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border border-[#2a2620] rounded-sm p-4">
          <p className="text-sm text-[#c9bca4] leading-relaxed">{a || "—"}</p>
        </div>
        <div className="border border-[#2a2620] rounded-sm p-4">
          <p className="text-sm text-[#c9bca4] leading-relaxed">{b || "—"}</p>
        </div>
      </div>
    </div>
  );
}


function ReportBlocks({ pub }) {
  return (
    <div className="mt-10 space-y-8" data-testid="research-report-blocks">
      {pub.executive_summary && (
        <div className="border-l-2 border-[#b8963e] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Executive Summary</p>
          <p className="mt-2 text-[#f4efe6] leading-relaxed">{pub.executive_summary}</p>
        </div>
      )}
      {pub.key_findings?.length > 0 && (
        <div>
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Key findings</p>
          <ul className="mt-3 space-y-3">
            {pub.key_findings.map((kf, i) => (
              <li key={i} className="flex gap-3 text-[#c9bca4] leading-relaxed">
                <span className="font-mono-eav text-[10px] text-[#b8963e] mt-1">0{i + 1}</span>
                <span>{kf}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {pub.related_reports?.length > 0 && (
        <div>
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Related reports</p>
          <ul className="mt-2 space-y-1">
            {pub.related_reports.map((s, i) => (
              <li key={i}>
                <Link to={`/research/reports/${s}`} className="text-[#c9bca4] hover:text-[#b8963e]">{s}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function CaseStudyBlocks({ pub }) {
  return (
    <div className="mt-10 space-y-8" data-testid="research-case-study-blocks">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatBlock label="INITIAL SCORE" value={`${pub.initial_score ?? "—"}/100`} />
        <StatBlock label="FINAL SCORE"   value={`${pub.final_score ?? "—"}/100`} accent />
      </div>
      {pub.findings && (
        <div className="border-l-2 border-[#5e5448] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">Findings</p>
          <p className="mt-2 text-[#c9bca4] leading-relaxed">{pub.findings}</p>
        </div>
      )}
      {pub.actions && (
        <div className="border-l-2 border-[#5e5448] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">Recommended actions</p>
          <p className="mt-2 text-[#c9bca4] leading-relaxed">{pub.actions}</p>
        </div>
      )}
      {pub.outcome && (
        <div className="border-l-2 border-[#b8963e] pl-6">
          <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#b8963e]">Outcome</p>
          <p className="mt-2 text-[#f4efe6] leading-relaxed">{pub.outcome}</p>
        </div>
      )}
    </div>
  );
}
